import CheckIcon from "@mui/icons-material/Check";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import CustomTooltip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../../../utils/constants";
import { sidebarIcons } from "../../environment/icons/iconExport";
import PotentialButton from "./IntegrationPotential/PotentialButton";

export default function IntegrationCard({
  route,
  image,
  name,
  codeName,
  description,
  status,
  available,
  configurable,
  loading,
  platformUserPermission,
  integrationsInfoQuery,
  integrationKey,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const [isIntegrated, setIsIntegrated] = useState(false);
  const navigate = useNavigate();
  const { removeIntegration, trackEvent } = useAPI();
  const [openDialog, setOpenDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const { permitted: hasModifyPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_INTEGRATION
  );

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onClick = (event) => {
    navigate(route);
  };

  const onViewGuide = () => {
    trackEvent("User click on Integration guide").then();
    navigate(route + "?viewonly");
    handleMenuClose();
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const onConfigure = () => {
    navigate(route);
    handleMenuClose();
  };

  const onRemoveClick = () => {
    const message =
      'User click on "Remove" button to remove the integration: ' + codeName;
    trackEvent(message).then();
    handleDialogOpen();
  };

  const confirmRemove = async () => {
    try {
      handleDialogClose();
      const message =
        'User click on "Remove" button to confirm the remove integration: ' +
        codeName;
      trackEvent(message).then();
      const response = await removeIntegration(codeName);
      // invalidate integration keys
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      if (response.status === 200) {
        setIsIntegrated(false);
        Toast.success(
          `The integration has been removed successfully. Data will be refreshed within approximately 24 hours.`
        );
      }
    } catch (error) {
      Toast.error(`Something went wrong, please contact support - ${error}`);
    }
  };

  useEffect(() => {
    if (available === true && status === true) {
      setIsIntegrated(true);
    } else {
      setIsIntegrated(false);
    }
  }, [available, status]);

  return (
    <Fragment>
      <Box
        sx={{
          width: 380,
          height: 260,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          borderRadius: "10px",
          boxShadow: "0px 1px 4px 0px " + colors.grey_48percent,
          backgroundColor: colors.common_white,
          padding: "24px",
          position: "relative",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              mb: "4px",
            }}
          >
            <SvgToIcon
              IconLogo={image}
              style={{ width: "32px", height: "32px" }}
            />
            {available && (
              <PotentialButton
                integrationName={codeName}
                integrationsInfoQuery={integrationsInfoQuery}
              />
            )}
          </Box>
          <Typography color={colors.text_primary} variant="subtitle1">
            {name}
          </Typography>
          <Typography
            color={colors.text_primary}
            variant="body2"
            sx={{ height: "80px" }}
          >
            {description}
          </Typography>
        </Box>

        <Divider />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "32px",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <LoadingIcon size={5} />
            </Box>
          ) : (
            <>
              {isIntegrated ? (
                <>
                  <IconButton
                    onClick={handleMenuClick}
                    size="small"
                    aria-controls={menuOpen ? "integration-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? "true" : undefined}
                  >
                    <MoreVertRoundedIcon
                      sx={{
                        width: "24px",
                        height: "24px",
                        fontSize: "20px",
                        color: colors.icon_action_active,
                      }}
                    />
                  </IconButton>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "default",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.success_dark,
                        fontWeight: 600,
                        mr: 0.5,
                      }}
                    >
                      Connected
                    </Typography>
                    <CheckIcon
                      sx={{
                        color: colors.success_dark,
                        fontSize: "1rem",
                      }}
                    />
                  </Box>
                </>
              ) : (
                <>
                  {available && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <CustomTooltip title="Integration guide" placement="top">
                        <IconButton onClick={onViewGuide} disabled={!route}>
                          <ChecklistRoundedIcon
                            sx={{
                              width: "24px",
                              height: "24px",
                              color: colors.icon_action_active,
                            }}
                          />
                        </IconButton>
                      </CustomTooltip>
                      <CustomToolTipWithPermission
                        title={
                          !platformUserPermission
                            ? VIEWER_ACCESS_DENIED_MESSAGE
                            : ""
                        }
                        permission={platformUserPermission}
                        placement="top"
                      >
                        <span>
                          <PrimaryButton
                            onClick={onClick}
                            disabled={!route || !platformUserPermission}
                            size="small"
                          >
                            Connect
                          </PrimaryButton>
                        </span>
                      </CustomToolTipWithPermission>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      <Menu
        id="integration-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "integration-menu-button",
        }}
        sx={{
          color: colors.contrast_text_dark,
          "& .MuiPaper-root": {
            borderRadius: "8px",
            minWidth: "150px",
            border: "1px solid " + colors.divider2,
            boxShadow: "0px 1px 4px 0px " + colors.black_10percent,
          },
          "& .MuiList-root": {
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          },
        }}
      >
        {configurable && (
          <MenuItem
            onClick={onConfigure}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <SvgToIcon
              IconLogo={sidebarIcons.SettingCenterIcon}
              style={{
                width: "16px",
                height: "16px",
                color: colors.icon_action_active,
              }}
            />
            <Typography variant="caption"> Configure </Typography>
          </MenuItem>
        )}
        <MenuItem
          onClick={onViewGuide}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <ChecklistRoundedIcon
            sx={{
              width: "16px",
              height: "16px",
              color: colors.icon_action_active,
            }}
          />
          <Typography variant="caption"> View Guide </Typography>
        </MenuItem>
        <CustomToolTipWithPermission
          title=""
          permission={hasModifyPermission}
          placement="top"
        >
          <Box>
            <MenuItem
              onClick={onRemoveClick}
              disabled={!hasModifyPermission}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <RemoveCircleOutlineIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.icon_action_active,
                }}
              />
              <Typography variant="caption"> Remove </Typography>
            </MenuItem>
          </Box>
        </CustomToolTipWithPermission>
      </Menu>

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to proceed? All related gaps will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemove}
            sx={{ color: colors.error_main, textTransform: "none" }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
